import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { PiFileTextLight } from 'react-icons/pi';
import apis from '../../utils/apis';

interface ConnectMatterModalProps {
  clientId: string;
  clientName: string;
  selectedMatterId: string;
  onChange: (value: string) => void;
  selectedRole: string;
  onRoleChange: (value: string) => void;
}

interface Matter {
  id: number;
  uuid: string;
  title?: string;
  name?: string;
  type?: string;
  status?: string;
}

const ConnectMatterModal: React.FC<ConnectMatterModalProps> = ({
  clientId,
  clientName,
  selectedMatterId,
  onChange,
  selectedRole,
  onRoleChange,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [existingMatters, setExistingMatters] = useState<DropdownOption[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [connectedMatterIds, setConnectedMatterIds] = useState<number[]>([]);
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [hasFetchedInitial, setHasFetchedInitial] = useState(false);

  // Fetch client's connected matters
  const fetchConnectedMatters = useCallback(async () => {
    try {
      const res = await apis({
        method: 'GET',
        url: `/clients/${clientId}/matters`,
      });

      if (res && res.data?.success && Array.isArray(res.data.matters)) {
        const connectedIds = res.data.matters.map((matter: Matter) => matter.uuid);
        setConnectedMatterIds(connectedIds);
      }
    } catch (err) {
      console.error('Error fetching connected matters:', err);
    }
  }, [clientId]);

  // Fetch role options
  const fetchRoleOptions = useCallback(async () => {
    if (roleLoading || roleOptions.length > 0) return; // Prevent multiple calls
    setRoleLoading(true);
    try {
      const res = await apis({
        method: 'GET',
        url: '/dropdown/client-roles',
      });

      if (res && res.data?.data && Array.isArray(res.data.data)) {
        const options = res.data.data.map((role: any) => ({
          value: role.id.toString(),
          label: role.name,
        }));
        setRoleOptions(options);
      }
    } catch (err) {
      console.error('Error fetching role options:', err);
    } finally {
      setRoleLoading(false);
    }
  }, [roleLoading, roleOptions.length]);

  // Fetch existing matters with pagination support
  const fetchMattersWithPagination = useCallback(async (query: string, page: number): Promise<DropdownOption[]> => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: '12',
        ...(query && { search: query }),
      });

      const res = await apis({
        method: 'GET',
        url: `/matters/view?${params.toString()}`,
      });

      let matters: Matter[] = [];
      let pageCount = 1;

      if (res && res.data) {
        if (Array.isArray(res.data.data)) {
          matters = res.data.data;
          pageCount = res.data.total_pages || 1;
        } else if (Array.isArray(res.data.matters)) {
          matters = res.data.matters;
          pageCount = res.data.total_pages || 1;
        } else if (Array.isArray(res.data)) {
          matters = res.data;
        }
      }

      setTotalPages(pageCount);

      const filteredMatters = matters.filter((matter: Matter) => !connectedMatterIds.includes(matter.id));

      return filteredMatters.map((matter: Matter) => ({
        value: matter.uuid,
        label: `${matter.title || matter.name || t('Untitled')} (${matter.type || t('No type')})`,
      }));
    } catch (err) {
      console.error('Error fetching matters:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [connectedMatterIds]);

  // Load initial data on mount
  useEffect(() => {
    if (!hasFetchedInitial) {
      setHasFetchedInitial(true);
      (async () => {
        await fetchConnectedMatters();
        await fetchRoleOptions();
        const initialMatters = await fetchMattersWithPagination('', 1);
        setExistingMatters(initialMatters);
      })();
    }
  }, [hasFetchedInitial]);

  return (
    <div className="rounded px-4">
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{clientName}</p>
      <div className="space-y-2">
        <Dropdown
          label={`${t('Select matter')} *`}
          options={existingMatters}
          value={selectedMatterId}
          onChange={(value) => onChange(typeof value === 'string' ? value : '')}
          placeholder={t('Select a matter')}
          useApi={true}
          fetchOptions={fetchMattersWithPagination}
          totalPages={totalPages}
          fullWidth={true}
          useLoadMoreButton={true}
        />
        <Dropdown
          label={`${t('Select role')} *`}
          options={roleOptions}
          value={selectedRole}
          onChange={(value) => onRoleChange(typeof value === 'string' ? value : '')}
          placeholder={t('Select a role')}
          fullWidth={true}
        />
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
          {t('Select an existing matter and role to connect it to this contact.')}
        </p>
      </div>
    </div>
  );
};

export default ConnectMatterModal;
